import { ActionRunner } from "applesauce-actions";
import type { NostrEvent } from "nostr-tools";
import { eventStore, publish } from "./nostr";
import { accounts } from "./accounts";

/**
 * Kinds that user index relays (purplepag.es, etc.) are known to accept.
 * These are profile, contact, relay, and list kinds. Kind 30078 (NIP-78 app
 * data) is intentionally excluded — index relays reject application data
 * events as they are not list information.
 */
const INDEX_RELAY_KINDS = new Set<number>([
  0, // profile metadata
  3, // contact / follow list
  10002, // NIP-65 relay list (mailboxes)
  10017, // NIP-51 Git authors follow list
  10018, // NIP-51 Git repositories follow list
  10317, // Grasp server list
]);

/**
 * Publish function passed to the ActionRunner.
 *
 * For profile, contact, relay, and list kinds, lookup/index relays are added
 * as a separate "User Index Relays" group so that the updated event reaches
 * well-connected index relays in addition to the user's own outbox relays.
 * This improves discoverability for other clients.
 *
 * Kind 30078 (NIP-78 app data) is intentionally excluded — index relays
 * reject application data events as they are not list information.
 *
 * For all other events the call is forwarded to publish() unchanged.
 */
function runnerPublish(event: NostrEvent, relays?: string[]): Promise<void> {
  if (INDEX_RELAY_KINDS.has(event.kind)) {
    return publish(event, ["index-relays"], { relayUrls: relays });
  }
  return publish(event, undefined, { relayUrls: relays });
}

/**
 * Global ActionRunner instance for executing pre-built Nostr actions.
 * Examples: UpdateProfile, CreateNote, etc.
 *
 * In Applesauce v6 the ActionRunner takes the signer directly — the
 * `EventFactory` singleton is gone, and actions instantiate typed factories
 * (e.g. `IssueFactory`, `ProfileFactory`) per-call.
 *
 * Relay-hint resolution (`getEventRelayHint` / `getPubkeyRelayHint`) now
 * lives in `src/factories/hints.ts` and is passed per-call into tag
 * operations such as `addProfilePointerTag(pubkey, getPubkeyRelayHint)`.
 *
 * Uses `accounts.signer` — a ProxySigner that automatically tracks the
 * active account, so switching accounts is reflected immediately without
 * recreating the runner.
 *
 * Usage:
 * ```ts
 * import { runner } from '@/services/actions';
 *
 * await runner.run(UpdateProfile, { name: 'Alice' });
 * ```
 */
export const runner = new ActionRunner(
  eventStore,
  accounts.signer,
  runnerPublish,
);
